import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, map, from, forkJoin, of } from 'rxjs';
import { switchMap, catchError, mergeMap, toArray, finalize } from 'rxjs/operators';
import { ListResult } from '../api/core/list-result';
import {
  QuestionnaireTemplateDto,
  CreateUpdateQuestionnaireTemplateDto,
  QuestionnaireFormFieldSectionDto,
  QuestionnaireFormFieldSection,
  QuestionnaireFormField,
  QuestionnaireUsageResult,
  SyncResultItem,
  SyncResult,
  SyncProgressCallback
} from '../api/questionnaire/questionnaire.template';
import { CreateUpdateFormDesignDto } from '../api/form-design/form-design';
import { FormDesignStatus, FieldUIType } from '../api/enum';
import { KeywordLookupItem } from '../api/lookup/keyword-lookup-item';
import { FormDefinition, FormTab } from '../components/shared/form-designer/models/form.model';
import { Section, Row, ColumnCount } from '../components/shared/form-designer/models/section.model';
import { Field } from '../components/shared/form-designer/models/field.model';
import { KeywordFieldExportInfo, SubFieldExportInfo } from '../components/shared/form-designer/models/keyword-field-export-info.model';
import { generateGuid } from '../components/shared/form-designer/utils/guid.util';
import { KeywordFieldStrategy } from '../components/shared/form-designer/field-types/keyword-field.strategy';
import { RepeatableKeywordFieldStrategy } from '../components/shared/form-designer/field-types/repeatable-keyword-field.strategy';
import { BACKEND_URL } from '../app.config';
import { VirtualField } from '../api/virtual-field/virtual-field';
import { LookupService, FormDesignService } from './';

@Injectable({
  providedIn: 'root'
})
export class QuestionnaireTemplateService {
  constructor(
    private readonly http: HttpClient,
    @Inject(BACKEND_URL) private readonly baseUrl: string,
    private readonly lookupService: LookupService,
    private readonly formDesignService: FormDesignService
  ) { }

  // GET /api/app/questionnaire
  getList(
    sorting: string = 'name asc',
    skipCount: number = 0,
    maxResultCount: number = 10
  ): Observable<ListResult<QuestionnaireTemplateDto>> {
    return this.http.get<ListResult<QuestionnaireTemplateDto>>(`${this.baseUrl}/api/app/questionnaire`, {
      params: {
        Sorting: sorting,
        SkipCount: skipCount,
        MaxResultCount: maxResultCount
      }
    });
  }

  // GET /api/app/questionnaire/simple-list
  getSimpleList(processModuleVersionId?: string): Observable<QuestionnaireTemplateDto[]> {
    const options = processModuleVersionId ? { params: { processModuleVersionId } } : {};
    return this.http.get<QuestionnaireTemplateDto[]>(`${this.baseUrl}/api/app/questionnaire/simple-list`, options);
  }

  // POST /api/app/questionnaire
  create(questionnaire: CreateUpdateQuestionnaireTemplateDto): Observable<QuestionnaireTemplateDto> {
    return this.http.post<QuestionnaireTemplateDto>(`${this.baseUrl}/api/app/questionnaire`, questionnaire);
  }

  // GET /api/app/questionnaire/{id}
  get(id: string): Observable<QuestionnaireTemplateDto> {
    return this.http.get<QuestionnaireTemplateDto>(`${this.baseUrl}/api/app/questionnaire/${id}`);
  }

  // PUT /api/app/questionnaire/{id}
  update(id: string, questionnaire: CreateUpdateQuestionnaireTemplateDto): Observable<QuestionnaireTemplateDto> {
    return this.http.put<QuestionnaireTemplateDto>(`${this.baseUrl}/api/app/questionnaire/${id}`, questionnaire);
  }

  // DELETE /api/app/questionnaire/{id}
  getReference(id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/api/app/questionnaire/${id}/reference`);
  }
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/app/questionnaire/${id}`);
  }

  // GET /api/app/questionnaire/form-fields/{id}
  getFormFields(id: string): Observable<QuestionnaireFormFieldSectionDto[]> {
    return this.http.get<any>(`${this.baseUrl}/api/app/questionnaire/form-fields/${id}`);
  }

  // GET /api/app/questionnaire/process-fields/{processModuleVersionId}
  getProcessFields(processModuleVersionId: string): Observable<QuestionnaireFormFieldSectionDto[]> {
    return this.http.get<QuestionnaireFormFieldSectionDto[]>(`${this.baseUrl}/api/app/questionnaire/process-fields/${processModuleVersionId}`);
  }

  /**
   * Generate FormDesign JSON for government form
   * This is the main public method that components will call
   * @param formId - The government form ID
   * @param keywords - All available keywords (passed from component)
   * @param columns - Number of columns for form layout (1-3), defaults to 2
   * @returns Observable with FormDesign data and keywords
   */
  generateFormDesignJson(
    formId: string,
    keywords: KeywordLookupItem[],
    columns: 1 | 2 | 3 = 2
  ): Observable<{ designData: string | null; designKeywords: KeywordFieldExportInfo[] }> {
    return this.getFormFields(formId).pipe(
      map((formFields) => {
        const matchedFields = this.matchFormFieldsWithKeywords(formFields, keywords);
        const designData = this.buildFormDesignJsonString(matchedFields, columns);
        const designKeywords = this.extractKeywordsFromMatchedFields(matchedFields);

        return {
          designData,
          designKeywords
        };
      })
    );
  }

  /**
   * Generate FormDesign JSON for process fields
   * This is the main public method that components will call
   * @param processModuleVersionId - The process module version ID
   * @param keywords - All available keywords (passed from component)
   * @param columns - Number of columns for form layout (1-3), defaults to 2
   * @returns Observable with FormDesign data and keywords
   */
  generateFormDesignFromProcessFields(
    processModuleVersionId: string,
    keywords: KeywordLookupItem[],
    columns: 1 | 2 | 3 = 2
  ): Observable<{ designData: string | null; designKeywords: KeywordFieldExportInfo[] }> {
    return this.getProcessFields(processModuleVersionId).pipe(
      map((formFields) => {
        const matchedFields = this.matchFormFieldsWithKeywords(formFields, keywords);
        const designData = this.buildFormDesignJsonString(matchedFields, columns, 'Process Fields');
        const designKeywords = this.extractKeywordsFromMatchedFields(matchedFields);

        return {
          designData,
          designKeywords
        };
      })
    );
  }

  /**
   * Public method: Build FormDesign JSON string from form field sections
   * Creates a FormDefinition compatible with FormDesigner component
   * @param formFields - Form field sections from API
   * @param keywords - All available keywords
   * @param columns - Number of columns for form layout (1-3)
   * @param tabTitle - Optional title for the tab, defaults to 'Section Form'
   * @returns FormDesign JSON string or null
   */
  public buildFormDesignFromSections(
    formFields: QuestionnaireFormFieldSectionDto[],
    keywords: KeywordLookupItem[],
    columns: 1 | 2 | 3 = 2,
    tabTitle: string = 'Section Form'
  ): string | null {
    const matchedFields = this.matchFormFieldsWithKeywords(formFields, keywords);
    return this.buildFormDesignJsonString(matchedFields, columns, tabTitle);
  }

  /**
   * Private method: Match form fields with keyword data
   * @param formFields - Original form field sections from API
   * @param keywords - All available keywords
   * @returns Matched form field sections with keyword data
   */
  private matchFormFieldsWithKeywords(
    formFields: QuestionnaireFormFieldSectionDto[],
    keywords: KeywordLookupItem[]
  ): QuestionnaireFormFieldSection[] {
    // Filter keywords to remove visibleInEditor = false fields
    const filteredKeywords = this.filterVisibleKeywords(keywords);

    const matchedSections: QuestionnaireFormFieldSection[] = [];
    const processedKeywords = new Set<string>(); // Global keywords set to track processed keywords

    formFields.forEach(originalSection => {
      const matchedFields: QuestionnaireFormField[] = [];

      originalSection.fields.forEach(originalField => {
        const keywordLower = originalField.keyword.toLowerCase();

        // Check if this keyword has already been processed
        if (processedKeywords.has(keywordLower)) {
          console.warn(`Duplicate keyword detected and ignored: ${originalField.keyword} in section: ${originalSection.section}`);
          return; // Skip duplicate keyword
        }

        const matchedKeyword = filteredKeywords.find(
          keyword => keyword.key.toLowerCase() === keywordLower
        );

        if (matchedKeyword) {
          processedKeywords.add(keywordLower); // Add to processed keywords set
          const matchedField: QuestionnaireFormField = {
            name: originalField.name,
            keyword: matchedKeyword.key,
            keywordProp: matchedKeyword
          };
          matchedFields.push(matchedField);
        } else {
          console.warn(`No matching keyword found for: ${originalField.keyword} in section: ${originalSection.section}`);
        }
      });

      if (matchedFields.length > 0) {
        const matchedSection: QuestionnaireFormFieldSection = {
          section: originalSection.section,
          fields: matchedFields
        };
        matchedSections.push(matchedSection);
      }
    });

    return matchedSections;
  }

  /**
   * Filter keyword fields to remove visibleInEditor = false fields
   * For Repeat List fields, also filter their sub-fields
   * @param keywords - All available keywords
   * @returns Filtered keywords with only visible fields
   */
  private filterVisibleKeywords(keywords: KeywordLookupItem[]): KeywordLookupItem[] {
    return keywords
      .filter(keyword => keyword.visibleInEditor !== false)
      .map(keyword => {
        // If it's a Repeat List field, filter its sub-fields as well
        if (keyword.uiType === 'Repeat List' && keyword.fields && keyword.fields.length > 0) {
          return {
            ...keyword,
            fields: this.filterVisibleKeywords(keyword.fields)
          };
        }
        return keyword;
      });
  }

  /**
   * Private method: Build FormDesign JSON string from matched form fields
   * Creates a FormDefinition compatible with FormDesigner component
   * @param matchedFields - Matched form field sections with keyword data
   * @param columns - Number of columns for form layout (1-3)
   * @param tabTitle - Optional title for the tab, defaults to 'Government Form'
   * @returns FormDesign JSON string or null
   */
  private buildFormDesignJsonString(matchedFields: QuestionnaireFormFieldSection[], columns: 1 | 2 | 3 = 2, tabTitle: string = 'Government Form'): string | null {
    if (matchedFields.length === 0) {
      return null;
    }

    // Create strategy instances
    const keywordStrategy = new KeywordFieldStrategy();
    const repeatableKeywordStrategy = new RepeatableKeywordFieldStrategy();

    // Create FormDefinition structure
    const formDefinition: FormDefinition = {
      id: generateGuid(),
      tabs: [
        {
          id: generateGuid(),
          title: tabTitle,
          sections: []
        }
      ]
    };

    // For each matchedSection, create a Section
    matchedFields.forEach(matchedSection => {
      const section: Section = {
        id: generateGuid(),
        title: matchedSection.section,
        rows: []
      };

      // Process fields in original order, but handle different types differently
      const regularFieldsBuffer: Field[] = [];

      matchedSection.fields.forEach(field => {
        // Check if this is a repeatable keyword field
        if (field.keywordProp?.uiType === 'Repeat List') {
          // First, flush any buffered regular fields
          this.addRegularFieldsToSection(regularFieldsBuffer, section, columns);
          regularFieldsBuffer.length = 0; // Clear buffer

          // Create repeatable keyword field
          const repeatableField: Field = repeatableKeywordStrategy.createField(
            generateGuid(),
            field.keywordProp
          );

          // Override label if custom name is provided
          if (field.name && field.name.trim() !== '') {
            repeatableField.label = field.name;
          }

          // Create special row for repeatable keyword field
          const repeatableRow: Row = {
            id: generateGuid(),
            columns: 2, // Always use 1 column for repeatable keyword fields
            fields: [[]], // Empty fields array since the field is stored in repeatableKeywordField
            isRepeatableRow: true,
            repeatableKeywordField: repeatableField
          };

          section.rows.push(repeatableRow);
        } else {
          // Regular field - add to buffer
          const formField: Field = keywordStrategy.createField(
            generateGuid(),
            field.keywordProp
          );

          // Override label if custom name is provided
          if (field.name && field.name.trim() !== '') {
            formField.label = field.name;
          }

          regularFieldsBuffer.push(formField);
        }
      });

      // Process any remaining regular fields in buffer
      this.addRegularFieldsToSection(regularFieldsBuffer, section, columns);

      formDefinition.tabs[0].sections.push(section);
    });

    return JSON.stringify(formDefinition, null, 2);
  }

  /**
   * Helper method: Add regular fields to section with proper column layout
   * @param fields - Array of regular fields to add
   * @param section - Section to add fields to
   * @param columns - Number of columns for layout
   */
  private addRegularFieldsToSection(fields: Field[], section: Section, columns: ColumnCount): void {
    if (fields.length === 0) {
      return;
    }

    // Distribute fields across rows based on column count
    for (let i = 0; i < fields.length; i += columns) {
      // Create fields array for this row with specified number of columns
      const rowFields: Field[][] = Array(columns).fill(null).map(() => []);

      // Distribute fields across columns for this row
      for (let j = 0; j < columns && (i + j) < fields.length; j++) {
        rowFields[j].push(fields[i + j]);
      }

      // Create row with multi-column layout
      const row: Row = {
        id: generateGuid(),
        columns: columns,
        fields: rowFields
      };

      section.rows.push(row);
    }
  }

  /**
   * Private method: Extract keyword field export information from matched form fields
   * @param matchedFields - Matched form field sections
   * @returns Array of keyword field export information
   */
  private extractKeywordsFromMatchedFields(matchedFields: QuestionnaireFormFieldSection[]): KeywordFieldExportInfo[] {
    const keywordExportInfoList: KeywordFieldExportInfo[] = [];
    const processedKeywords = new Set<string>();

    matchedFields.forEach(section => {
      section.fields.forEach(field => {
        if (field.keyword && field.keywordProp && !processedKeywords.has(field.keyword)) {
          processedKeywords.add(field.keyword);

          const exportInfo: KeywordFieldExportInfo = {
            keyword: field.keywordProp.key,
            readonly: !field.keywordProp.isWritable,
            required: field.keywordProp.required
          };

          // Only set fields property for Repeat List type
          if (field.keywordProp.uiType === 'Repeat List') {
            exportInfo.fields = this.convertSubFieldsToExportInfo(field.keywordProp.fields);
          }

          keywordExportInfoList.push(exportInfo);
        }
      });
    });

    return keywordExportInfoList;
  }

  /**
   * Helper method: Convert sub-fields to SubFieldExportInfo array
   * @param subFields - Array of KeywordLookupItem sub-fields
   * @returns Array of SubFieldExportInfo or undefined if no visible fields
   */
  private convertSubFieldsToExportInfo(subFields: KeywordLookupItem[] | undefined): SubFieldExportInfo[] | undefined {
    if (!subFields || subFields.length === 0) {
      return undefined;
    }

    // Filter out fields with visibleInEditor = false
    const visibleFields = subFields.filter(field => field.visibleInEditor !== false);

    if (visibleFields.length === 0) {
      return undefined;
    }

    return visibleFields.map(field => ({
      fieldName: field.key,
      readonly: !field.isWritable,
      required: field.required,
      hidden: false
    }));
  }

  /**
   * Create empty FormDesign JSON structure
   * This creates a basic form with one tab and one empty section for better user experience
   * @returns FormDesign JSON string with empty structure
   */
  createEmptyFormDesign(): string {
    // Create basic FormDefinition structure with one tab and one empty section
    const formDefinition: FormDefinition = {
      id: generateGuid(),
      tabs: [
        {
          id: generateGuid(),
          title: 'Tab 1',
          sections: [
            {
              id: generateGuid(),
              title: 'Section 1',
              rows: []
            }
          ]
        }
      ]
    };

    return JSON.stringify(formDefinition, null, 2);
  }

  /**
   * Synchronize Virtual Field settings to Questionnaires
   * Updates displayName, defaultValue, and options in FormDesign JSON
   * @param virtualField - The Virtual Field to sync
   * @param questionnaireUsages - Array of questionnaires using this virtual field
   * @param onProgress - Optional callback to report progress
   * @returns Observable with sync results
   */
  syncVirtualFieldToQuestionnaires(
    virtualField: VirtualField,
    questionnaireUsages: QuestionnaireUsageResult[],
    onProgress?: SyncProgressCallback
  ): Observable<SyncResult> {
    if (onProgress) {
      onProgress('Synchronizing...');
    }

    return from(questionnaireUsages).pipe(
      mergeMap(usage =>
        this.lookupService.getKeywords(
          [virtualField.dynamicKeywordName!],
          undefined,
          false,
          false,
          false,
          true,
          usage.audience,
        ).pipe(
          switchMap(result => {
            if (!result.items?.length) throw new Error('Keyword not found');
            const keywordItem = result.items[0];
            return this.syncSingleQuestionnaire(keywordItem, usage, virtualField.name || '');
          }),
          catchError(() => of({
            questionnaireName: usage.name,
            formStatus: usage.formDesign.status,
            syncStatus: 'Failed' as const
          }))
        ),
        3
      ),
      toArray(),
      map(items => ({ items }))
    );
  }

  /**
   * Sync a single questionnaire
   * @param virtualField - The Virtual Field to sync
   * @param keywordItem - The keyword lookup item
   * @param usage - The questionnaire usage info
   * @returns Observable with sync result item
   */
  private syncSingleQuestionnaire(
    keywordItem: KeywordLookupItem,
    usage: QuestionnaireUsageResult,
    virtualFieldName: string
  ): Observable<SyncResultItem> {
    try {
      // Parse FormDesign JSON
      const formDefinition: FormDefinition = JSON.parse(usage.formDesign.data);

      // Determine if this is a repeatable field
      const isRepeatableSubField = keywordItem.uiType && keywordItem.uiType === 'Repeat List';

      // Update the field in FormDesign
      let fieldUpdated = false;

      if (isRepeatableSubField) {
        // Update repeatable sub-field
        fieldUpdated = this.updateRepeatableSubField(
          formDefinition,
          keywordItem,
          virtualFieldName
        );
      } else {
        // Update regular field
        fieldUpdated = this.updateRegularField(
          formDefinition,
          keywordItem
        );
      }

      if (!fieldUpdated) {
        return of({
          questionnaireName: usage.name,
          formStatus: usage.formDesign.status,
          syncStatus: 'Failed' as const
        });
      }

      // Extract keywords using static method
      const keywords = QuestionnaireTemplateService.extractKeywordFieldsFromFormDefinition(formDefinition);

      // Prepare update DTO
      const updateDto: CreateUpdateFormDesignDto = {
        data: JSON.stringify(formDefinition, null, 2),
        keywords: keywords,
        notes: usage.formDesign.notes,
        entityType: usage.formDesign.entityType,
        entityId: usage.formDesign.entityId
      };

      // Update FormDesign
      return this.formDesignService.update(usage.formDesign.id, updateDto).pipe(
        switchMap((updatedFormDesign) => {
          // If status is Published, republish
          if (usage.formDesign.status === FormDesignStatus.Published) {
            return this.formDesignService.publish(updatedFormDesign.id).pipe(
              map(() => ({
                questionnaireName: usage.name,
                formStatus: FormDesignStatus.Published,
                syncStatus: 'Success' as const
              }))
            );
          } else {
            return of({
              questionnaireName: usage.name,
              formStatus: FormDesignStatus.Draft,
              syncStatus: 'Success' as const
            });
          }
        }),
        catchError(() => of({
          questionnaireName: usage.name,
          formStatus: usage.formDesign.status,
          syncStatus: 'Failed' as const
        }))
      );
    } catch (error) {
      return of({
        questionnaireName: usage.name,
        formStatus: usage.formDesign.status,
        syncStatus: 'Failed' as const
      });
    }
  }

  /**
   * Update a regular field in FormDesign
   * @param formDefinition - The form definition
   * @param virtualField - The Virtual Field
   * @param keywordItem - The keyword item
   * @returns true if field was found and updated
   */
  private updateRegularField(
    formDefinition: FormDefinition,
    keywordItem: KeywordLookupItem
  ): boolean {
    let fieldFound = false;

    // Iterate through tabs, sections, and rows
    formDefinition.tabs.forEach(tab => {
      tab.sections.forEach(section => {
        section.rows.forEach(row => {
          // Check regular fields
          if (row.fields) {
            row.fields.forEach(column => {
              column.forEach((field: any) => {
                if (field.key.toLowerCase() === keywordItem.key.toLowerCase()) {
                  // Update field properties
                  field['defaultValue'] = keywordItem.defaultValue;
                  field['required'] = keywordItem.required;
                  if (keywordItem.maxLength) {
                    field['maxLength'] = keywordItem.maxLength;
                  }

                  if (keywordItem.uiType == 'Choice') {
                    // Update options if present
                    if (keywordItem.options && keywordItem.options.length > 0) {
                      field['options'] = keywordItem.options;
                    }

                    if (keywordItem.allowMultiple) {
                      field['renderType'] = 'multiple_choice';
                      field['allowMultiple'] = true;
                    } else {
                      field['renderType'] = "dropdown";
                      field['allowMultiple'] = false;
                    }
                  }

                  fieldFound = true;
                }
              });
            });
          }
        });
      });
    });

    return fieldFound;
  }

  /**
   * Update a repeatable sub-field in FormDesign
   * @param formDefinition - The form definition
   * @param virtualField - The Virtual Field
   * @param keywordItem - The keyword item
   * @returns true if field was found and updated
   */
  private updateRepeatableSubField(
    formDefinition: FormDefinition,
    keywordItem: KeywordLookupItem,
    subFieldName: string
  ): boolean {
    let fieldFound = false;
    const repeatableKeywordStrategy = new RepeatableKeywordFieldStrategy();

    // Iterate through tabs, sections, and rows
    formDefinition.tabs.forEach(tab => {
      tab.sections.forEach(section => {
        section.rows.forEach(row => {
          // Check repeatable keyword field
          if (row.isRepeatableRow && row.repeatableKeywordField) {
            const repeatableField: any = row.repeatableKeywordField;

            // Check if this is the correct repeatable keyword
            if (repeatableField.key.toLowerCase() === keywordItem.key.toLowerCase()) {
              const newRepeatableField: Field = repeatableKeywordStrategy.createField(
                repeatableField.id,
                keywordItem
              );

              // update enableItemSelection property
              repeatableField['enableItemSelection'] = newRepeatableField['enableItemSelection'];
              repeatableField['selectionFieldKey'] = newRepeatableField['selectionFieldKey'];

              // Find and update the sub-field in subFields array
              if (repeatableField['subFields']) {
                const subFieldIndex = repeatableField['subFields'].findIndex((sf: any) => sf['key'] === subFieldName);
                if (subFieldIndex !== -1) {
                  const newSubField = newRepeatableField['subFields']?.find((sf: any) => sf['key'] === subFieldName);

                  if (newSubField) {
                    const findField = repeatableField['subFields'][subFieldIndex];
                    findField['defaultValue'] = newSubField.defaultValue;
                    findField['maxLength'] = newSubField.maxLength;
                    findField['required'] = newSubField.required;
                    findField['title'] = newSubField.title;

                    if (newSubField.uiType == 'Choice') {
                      findField['options'] = newSubField.options;
                      findField['allowMultiple'] = newSubField.allowMultiple;
                      if (newSubField.allowMultiple) {
                        findField['renderType'] = 'multiple_choice';
                      }
                    }
                    fieldFound = true;
                  }
                }
              }

              // Find and update the sub-field in createdSubFields array
              if (repeatableField['createdSubFields']) {
                const createdSubFieldIndex = repeatableField['createdSubFields'].findIndex((sf: any) => sf['key'] === subFieldName);
                if (createdSubFieldIndex !== -1) {
                  const newCreatedSubField = newRepeatableField['createdSubFields']?.find((sf: any) => sf['key'] === subFieldName);
                  if (newCreatedSubField) {
                    const findField = repeatableField['createdSubFields'][createdSubFieldIndex];

                    findField['defaultValue'] = newCreatedSubField.defaultValue;
                    findField['maxLength'] = newCreatedSubField.maxLength;
                    findField['required'] = newCreatedSubField.required;

                    if (newCreatedSubField.uiType == 'Choice') {
                      findField['allowMultiple'] = newCreatedSubField.allowMultiple;
                      findField['options'] = newCreatedSubField.options;
                      findField['choiceDisplayType'] = newCreatedSubField.choiceDisplayType;
                    }

                    fieldFound = true;
                  }
                }
              }

              // If field not found in existing arrays, add it as a new sub-field
              if (!fieldFound) {
                const newSubField = newRepeatableField['subFields']?.find((sf: any) => sf['key'] === subFieldName);
                const newCreatedSubField = newRepeatableField['createdSubFields']?.find((sf: any) => sf['key'] === subFieldName);

                if (newSubField && repeatableField['subFields']) {
                  repeatableField['subFields'].push(newSubField);
                }
                if (newCreatedSubField && repeatableField['createdSubFields']) {
                  repeatableField['createdSubFields'].push(newCreatedSubField);
                }

                if (newSubField) {
                  fieldFound = true;
                }
              }
            }
          }
        });
      });
    });

    return fieldFound;
  }

  /**
   * Extract keyword fields from form definition
   * Public static method for use across the application
   * @param formDefinition - The form definition to extract keywords from
   * @returns Array of keyword field export information
   */
  static extractKeywordFieldsFromFormDefinition(formDefinition: FormDefinition): KeywordFieldExportInfo[] {
    const keywordFields: KeywordFieldExportInfo[] = [];
    const processedKeys = new Set<string>(); // Track processed keys to avoid duplicates

    formDefinition.tabs.forEach(tab => {
      tab.sections.forEach(section => {
        section.rows.forEach(row => {
          // Handle regular keyword fields
          row.fields.forEach(columnFields => {
            columnFields.forEach(field => {
              if (field.type === 'keyword_field' && field.key && !processedKeys.has(field.key)) {
                processedKeys.add(field.key);
                keywordFields.push({
                  keyword: field.key,
                  readonly: field.readOnly || false,
                  required: field.required || false
                  // fields property is undefined for regular keyword fields
                });
              }
            });
          });

          // Handle repeatable keyword field (REPEATABLE_KEYWORD_FIELD type)
          if (row.isRepeatableRow && row.repeatableKeywordField && row.repeatableKeywordField.key && !processedKeys.has(row.repeatableKeywordField.key)) {
            processedKeys.add(row.repeatableKeywordField.key);

            const subFields: SubFieldExportInfo[] = [];
            const createdSubFields = (row.repeatableKeywordField as any).createdSubFields;

            if (createdSubFields && Array.isArray(createdSubFields)) {
              createdSubFields.forEach((subField: Field) => {
                subFields.push({
                  fieldName: subField.key,
                  readonly: subField.readOnly || false,
                  required: subField.required || false,
                  hidden: (subField as any).hidden || false
                });
              });
            }

            keywordFields.push({
              keyword: row.repeatableKeywordField.key,
              readonly: row.repeatableKeywordField.readOnly || false,
              required: row.repeatableKeywordField.required || false,
              fields: subFields.length > 0 ? subFields : undefined
            });
          }
        });
      });
    });

    return keywordFields;
  }

  /**
   * Extract keyword field keys from form definition
   * Public static method for use across the application
   * @param formDefinition - The form definition to extract keyword keys from
   * @returns Array of unique keyword keys
   */
  static extractKeywordFieldKeys(formDefinition: FormDefinition): string[] {
    const keys: string[] = [];

    formDefinition.tabs.forEach(tab => {
      tab.sections.forEach(section => {
        section.rows.forEach(row => {
          row.fields.forEach(columnFields => {
            columnFields.forEach(field => {
              if (field.type === 'keyword_field' && field.key) {
                keys.push(field.key);
              }
            });
          });

          // Handle repeatable keyword field (REPEATABLE_KEYWORD_FIELD type)
          if (row.isRepeatableRow && row.repeatableKeywordField && row.repeatableKeywordField.key) {
            keys.push(row.repeatableKeywordField.key);
          }
        });
      });
    });

    return [...new Set(keys)]; // Remove duplicates
  }

  /**
   * Extract keyword field keys from a tab
   * Public static method for use across the application
   * @param tab - The tab to extract keyword keys from
   * @returns Array of unique keyword keys
   */
  static extractKeywordFieldKeysFromTab(tab: FormTab): string[] {
    const keys: string[] = [];

    tab.sections.forEach(section => {
      section.rows.forEach(row => {
        // Extract from regular fields
        row.fields.forEach(columnFields => {
          columnFields.forEach(field => {
            if (field.type === 'keyword_field' && field.key) {
              keys.push(field.key);
            }
          });
        });

        // Extract from repeatable keyword field
        if (row.isRepeatableRow && row.repeatableKeywordField && row.repeatableKeywordField.key) {
          keys.push(row.repeatableKeywordField.key);
        }
      });
    });

    return [...new Set(keys)];
  }

  /**
   * Extract keyword field keys from a section
   * Public static method for use across the application
   * @param section - The section to extract keyword keys from
   * @returns Array of unique keyword keys
   */
  static extractKeywordFieldKeysFromSection(section: Section): string[] {
    const keys: string[] = [];

    section.rows.forEach(row => {
      // Extract from regular fields
      row.fields.forEach(columnFields => {
        columnFields.forEach(field => {
          if (field.type === 'keyword_field' && field.key) {
            keys.push(field.key);
          }
        });
      });

      // Extract from repeatable keyword field
      if (row.isRepeatableRow && row.repeatableKeywordField && row.repeatableKeywordField.key) {
        keys.push(row.repeatableKeywordField.key);
      }
    });

    return [...new Set(keys)];
  }

  /**
   * Synchronize Keyword settings to Questionnaires
   * Updates displayName, defaultValue, required, maxLength, options, and allowMultiple in FormDesign JSON
   * @param keyword - The Keyword to sync
   * @param questionnaireUsages - Array of questionnaires using this keyword
   * @param onProgress - Optional callback to report progress
   * @returns Observable with sync results
   */
  syncKeywordToQuestionnaires(
    keyword: KeywordLookupItem,
    questionnaireUsages: QuestionnaireUsageResult[],
    onProgress?: SyncProgressCallback
  ): Observable<SyncResult> {
    if (onProgress) {
      onProgress('Synchronizing...');
    }

    return from(questionnaireUsages).pipe(
      mergeMap(usage =>
        this.lookupService.getKeywords(
          [keyword.key],
          undefined,
          false,
          false,
          false,
          true,
          usage.audience,
        ).pipe(
          switchMap(result => {
            if (!result.items?.length) throw new Error('Keyword not found');
            const keywordItem = result.items[0];
            return this.syncSingleQuestionnaireForKeyword(keywordItem, usage);
          }),
          catchError(() => of({
            questionnaireName: usage.name,
            formStatus: usage.formDesign.status,
            syncStatus: 'Failed' as const
          }))
        ),
        3
      ),
      toArray(),
      map(items => ({ items }))
    );
  }

  /**
   * Sync a single questionnaire for keyword
   * @param keywordItem - The keyword lookup item
   * @param usage - The questionnaire usage info
   * @returns Observable with sync result item
   */
  private syncSingleQuestionnaireForKeyword(
    keywordItem: KeywordLookupItem,
    usage: QuestionnaireUsageResult
  ): Observable<SyncResultItem> {
    try {
      // Parse FormDesign JSON
      const formDefinition: FormDefinition = JSON.parse(usage.formDesign.data);

      // Update the keyword field in FormDesign
      const fieldUpdated = this.updateKeywordField(
        formDefinition,
        keywordItem
      );

      if (!fieldUpdated) {
        return of({
          questionnaireName: usage.name,
          formStatus: usage.formDesign.status,
          syncStatus: 'Failed' as const
        });
      }

      // Extract keywords using static method
      const keywords = QuestionnaireTemplateService.extractKeywordFieldsFromFormDefinition(formDefinition);

      // Prepare update DTO
      const updateDto: CreateUpdateFormDesignDto = {
        data: JSON.stringify(formDefinition),
        keywords: keywords,
        notes: usage.formDesign.notes,
        entityType: usage.formDesign.entityType,
        entityId: usage.formDesign.entityId
      };

      // Update FormDesign
      return this.formDesignService.update(usage.formDesign.id, updateDto).pipe(
        switchMap((updatedFormDesign) => {
          // If status is Published, republish
          if (usage.formDesign.status === FormDesignStatus.Published) {
            return this.formDesignService.publish(updatedFormDesign.id).pipe(
              map(() => ({
                questionnaireName: usage.name,
                formStatus: FormDesignStatus.Published,
                syncStatus: 'Success' as const
              }))
            );
          } else {
            return of({
              questionnaireName: usage.name,
              formStatus: FormDesignStatus.Draft,
              syncStatus: 'Success' as const
            });
          }
        }),
        catchError(() => of({
          questionnaireName: usage.name,
          formStatus: usage.formDesign.status,
          syncStatus: 'Failed' as const
        }))
      );
    } catch (error) {
      return of({
        questionnaireName: usage.name,
        formStatus: usage.formDesign.status,
        syncStatus: 'Failed' as const
      });
    }
  }

  /**
   * Update a keyword field in FormDesign
   * Supports all keyword types except Repeat List and Complex
   * @param formDefinition - The form definition
   * @param keywordItem - The keyword item
   * @returns true if field was found and updated
   */
  private updateKeywordField(
    formDefinition: FormDefinition,
    keywordItem: KeywordLookupItem
  ): boolean {
    // Skip unsupported types
    if (keywordItem.uiType === 'Repeat List' || keywordItem.uiType === 'Complex') {
      return false;
    }

    // For regular keyword fields, reuse updateRegularField logic
    return this.updateRegularField(formDefinition, keywordItem);
  }
}
